import * as THREE from 'three';
import { GearState } from './enum';

// 2D 点
export type Point = { x: number; y: number };

// 3D 点（用于高度/朝向等）
export interface Point3D {
  x: number;
  y: number;
  z: number;
}

export type Polygon = Point3D[];

// 导入枚举类型（从 proto 同步）
import {
  ObstacleType as EnumObstacleType,
  BehaviorIntention as EnumBehaviorIntention,
  StaticStatus as EnumStaticStatus,
  InteractionIntention as EnumInteractionIntention,
  MotionIntention as EnumMotionIntention,
  IntentType as EnumIntentType,
  ObstacleIntent as EnumObstacleIntent,
} from './enum';

export { EnumObstacleType as ObstacleType };
export { EnumBehaviorIntention as BehaviorIntention };
export { EnumStaticStatus as StaticStatus };
export { EnumInteractionIntention as InteractionIntention };
export { EnumMotionIntention as MotionIntention };
export { EnumIntentType as IntentType };
export type ObstacleIntent = EnumObstacleIntent;

export type DrivingMode = 'AUTO' | 'MANUAL' | string;

export interface CarInfo {
  /** 车辆朝向（3D 向量），通常取 z 分量作为 yaw 角 */
  orientation: Point3D;
  /** 当前线速度，单位：m/s */
  velocity: number;
  /** 方向盘/转向角（rad 或度，项目中常称为 steerAngle），可选 */
  steerAngle?: number;
  /** 驾驶模式，例如 'AUTO' 或 'MANUAL' 等 */
  drivingMode: DrivingMode;
  /** 档位状态，使用 `GearState` 枚举 */
  gearPosition: GearState;
  /** 线性加速度，单位：m/s^2，可选 */
  acceleration?: number;
  /** 转向灯状态（代码中可能为数字或字符串枚举），可选 */
  turnSignalState?: number | string;
  /** 车辆轮廓多边形（3D 点数组），用于绘制车辆占据空间，可选 */
  polygon?: Polygon;
}

export interface BehaviorDecision {
  /** 行为类型（枚举或字符串） */
  type?: number | string;
  /** 行为状态（枚举或字符串） */
  state?: number | string;
  /** 行为产生的原因或说明文字 */
  reason?: string;
  /** 调试信息字符串，便于开发/排查 */
  debugInfo?: string;
  /** 与此行为有交互关系的障碍物 id 列表 */
  interactiveObstacleId?: string[];
  /** 其它任意扩展字段 */
  [k: string]: any;
}

export interface PlanningDecision {
  /** 行为决策列表（按优先级或时间序列） */
  behaviorDecisionList?: BehaviorDecision[];
  /** 预计/规定的停止时间（单位依上游定义，可能为 s 或 ms） */
  stopTime?: number;
  /** 是否判定位于路口（交叉口） */
  isInIntersection?: boolean;
  /** 是否判定在大门前（园区入口/出口） */
  isInFrontOfGate?: boolean;
  /** 是否位于园区内部 */
  isInPark?: boolean;
  /** 其它任意扩展字段 */
  [k: string]: any;
}

export interface Planning {
  /** 规划得到的路径（点序列，通常为车参考点或轨迹点） */
  path: Point3D[];
  /** 规划轨迹（带时间或采样顺序的点序列），可选 */
  trajectory?: Point3D[];
  /** 停车目标点集合（用于泊车场景），可选 */
  parkingGoal?: ParkingGoal[];
  /** 规划阶段的决策信息（行为/停止等），可选 */
  decision?: PlanningDecision;
  /** 规划关注的红绿灯列表，用于高亮或优先级决策 */
  interestedLight?: PlanningTrafficLight[];
  /** 其它任意扩展字段 */
  [k: string]: any;
}

export interface Routing {
  /** 路由中心线点序列（可选），通常用于更高层级导航展示 */
  routingLine?: Point3D[];
  /** 其它任意扩展字段 */
  [k: string]: any;
}

export interface Lane {
  /** 车道唯一标识 */
  id: string;
  /** 车道中心/引导线点序列 */
  guideLine: Point3D[];
  /** 其它任意扩展字段 */
  [k: string]: any;
}

export interface Obstacle {
  /** 障碍物唯一 id */
  id: string;
  /** 障碍物轮廓多边形（3D 点数组），可选 */
  polygon?: Polygon;
  /** 障碍物类型（与 proto 定义的枚举对齐），可选 */
  obstacleType?: EnumObstacleType;
  /** 障碍物的未来轨迹点序列（预测轨迹），可选 */
  trajectory?: Point3D[];
  /** 障碍物高度（单位：米），可选 */
  height?: number;
  /** 障碍物朝向，弧度制（rad），可选 */
  heading?: number;
  /** 障碍物线速度（m/s），可选 */
  velocity?: number;
  /** 速度方向枚举或值（字符串或数字，取决上游实现），可选 */
  velocityHeading?: number | string;
  /** 行为意图（proto 枚举），可选 */
  behaviorIntention?: EnumBehaviorIntention;
  /** 交互意图（proto 枚举），可选 */
  interactionIntention?: EnumInteractionIntention;
  /** 运动意图（proto 枚举），可选 */
  motionIntention?: EnumMotionIntention;
  /** 障碍物意图（proto 枚举），可选 */
  obstacleIntent?: EnumObstacleIntent;
  /** 车辆灯光状态（位域或枚举），用于显示灯光，非必需 */
  vehicleLightState?: number;
  /** 其它任意扩展字段 */
  [k: string]: any;
}

// 规划关注的交通灯简化结构
export interface PlanningTrafficLight {
  /** 红绿灯 id，对应地图或感知数据中的标识 */
  id: string;
  /** 状态来源标识（如感知/地图/模拟等），可能为字符串或数字 */
  source?: string | number;
  /** 其它任意扩展字段 */
  [k: string]: any;
}

// 停车目标（parkingGoal）结构
export interface ParkingGoal {
  /** 停车目标点 x 坐标（通常为地图/局部坐标） */
  x: number;
  /** 停车目标点 y 坐标 */
  y: number;
  /** 可选 z 坐标（高度） */
  z?: number;
  /** 可选朝向 theta（弧度或角度，取决上游） */
  theta?: number;
  /** 其它任意扩展字段 */
  [k: string]: any;
}

export interface BoundaryStyleItem {
  /** 边界线样式类型，如 'SOLID'（实线）或 'DASH'（虚线） */
  type?: 'SOLID' | 'DASH' | string;
  /** 其它样式扩展字段 */
  [k: string]: any;
}

export interface BoundaryLine {
  /** 边界唯一标识 */
  id: string;
  /** 边界折线点集（2D 点数组） */
  line: Point[];
  /** 与边界相关的样式条目数组，可选 */
  style?: BoundaryStyleItem[];
  /** 其它任意扩展字段 */
  [k: string]: any;
}

export interface AuthInfo {
  /** 车辆 id（上游数据中的 vehicle_id 映射） */
  vehicleId?: string;
  /** 车辆序列号（vehicle_sn） */
  vehicleSn?: string;
  /** API key（用于鉴权），可选 */
  apiKey?: string;
  /** 其它任意扩展字段 */
  [k: string]: any;
}

export interface ProcessInfo {
  /** 代理/中间件时间戳（上游字段 proxy_timestamp），可能是 number 或字符串 */
  proxyTimestamp?: number | string;
  /** 其它任意扩展字段 */
  [k: string]: any;
}

// Stop line (停止线) 定义
export interface StopLine {
  /** 停止线唯一 id */
  id: string;
  /** 停止线样式标识（字符串或数字），可选 */
  style?: string | number;
  /** 停止线点集合（3D 点数组），用于绘制停止线位置 */
  point: Point3D[];
  /** 其它任意扩展字段 */
  [k: string]: any;
}

// 红绿灯定义（详细）
export interface TrafficLight {
  /** 红绿灯唯一 id */
  id: string;
  /** 当前灯色/状态编码（例如 1:red,2:green,3:yellow,4:off 等） */
  light?: number | string;
  /** 灯位位置坐标（3D 点），可选 */
  position?: Point3D;
  /** 灯位朝向/角度，单位依据上游定义（弧度或角度），可选 */
  angle?: number;
  /** 灯剩余时间（秒），用于显示倒计时，非必需 */
  remainingSecs?: number;
  /** 灯保持时间或周期时长，非必需 */
  duration?: number;
  /** 多帧状态或复合状态标识，可能为数字或字符串，非必需 */
  multiFrameState?: number | string;
  /** 其它任意扩展字段 */
  [k: string]: any;
}

// 噪声点 / Lidar 点信息（camelCase: point3d, sourceLidar）
export interface NoisePointInfo {
  /** 噪声点的 3D 坐标 */
  point3d: Point3D;
  /** 来源雷达/传感器标识（可选） */
  sourceLidar?: string;
  /** 其它任意扩展字段 */
  [k: string]: any;
}

// 多视角相机感知（天气/时间/区域）
export interface MultiviewCameraPerception {
  /** 多视角相机感知的时间类型（枚举或编码），可选 */
  scTimeType?: number | string;
  /** 多视角相机感知的天气类型（枚举或编码），可选 */
  scWeatherType?: number | string;
  /** 多视角相机感知的区域类型（枚举或编码），可选 */
  scRegionType?: number | string;
  /** 其它任意扩展字段 */
  [k: string]: any;
}

export interface ThreeMonitorData {
  /** 地图/视图原点偏移（2D），用于在屏幕或 canvas 上定位显示，可选 */
  origin?: Point;
  /** 当前车辆信息（位姿/速度/档位等），可选 */
  car?: CarInfo;
  /** 规划信息（路径/轨迹/决策等），可选 */
  planning?: Planning;
  /** 路由信息（高层导航线路），可选 */
  routing?: Routing;
  /** 车道集合（地图或感知提供），可选 */
  lane?: Lane[];
  /** 障碍物列表（感知或预测结果），可选 */
  obstacle?: Obstacle[];
  /** 停止线集合，用于路口控制展示，可选 */
  stopLine?: StopLine[];
  /** 红绿灯列表，可能包含状态/位置等，非必需 */
  trafficLight?: TrafficLight[];
  /** 原始/解包后的噪声点（点云）信息数组，非必需 */
  noisePointInfo?: NoisePointInfo[];
  /** 多视角相机的感知元信息（天气/时间/区域等），可选 */
  multiviewCameraPerception?: MultiviewCameraPerception;
  /** 原始消息时间戳（字符串或数字字符串），来源于上游数据 */
  timestamp?: string;
  /** 消息创建时间戳（字符串），上游 createTimestamp 字段 */
  createTimestamp?: string;
  /** 地图/视图边界线集合，可选 */
  boundary?: BoundaryLine[];
  /** 原点中心，用于一些坐标转换或视图居中，2D 点，可选 */
  originCenter?: Point;
  /** 鉴权/车辆信息（vehicleId/apiKey 等），可选 */
  authInfo?: AuthInfo;
  /** 进程/代理相关信息（如 proxyTimestamp 等），可选 */
  processInfo?: ProcessInfo;
  /** 顶部激光雷达时间戳（字符串），可选 */
  topLidarTimestamp?: string;
  /** MCP（多视角相机/元信息）原始对象，用于 UI 更新或展示，非必需 */
  mcp?: Record<string, any>;
  /** 其它任意扩展字段 */
  [k: string]: any;
}

export default ThreeMonitorData;

export interface PackedFrame {
  cmd: 'frame_geo';
  // 原始消息时间戳，worker 中来源于 message.timestamp
  timestamp: string | undefined;
  // 原始消息创建时间戳，worker 中来源于 message.createTimestamp
  createTimestamp: string | undefined;
  // 可选的帧 ID（由主线程发送时附加），用于在主/worker 之间精确配对
  frameId?: string | number;
  meta: PackedMeta;
  bufs: PackedBuffers;
}

export interface PackedBuffers {
  obsLine: ArrayBuffer;
  obsLineCol: ArrayBuffer;
  obsSurface: ArrayBuffer;
  obsSurfCol: ArrayBuffer;
  obsIndex: ArrayBuffer; // Uint32Array.buffer
  narrowLinePos: ArrayBuffer;
  narrowLineCol: ArrayBuffer;
  wideLinePos: ArrayBuffer;
  wideLineCol: ArrayBuffer;
  noisePos: ArrayBuffer;
  noiseCol: ArrayBuffer;
  obsAllIcons: ArrayBuffer; // 所有障碍物图标位置的缓冲
  // 所有起终点图标位置缓冲（同 obsAllIcons 的格式：连续的 xyz 浮点）
  startEndIcons?: ArrayBuffer;
  // 红绿灯图标位置缓冲（按 key 分段，段信息见 meta.trafficLightIconRanges）
  trafficLightAllIcons?: ArrayBuffer;
}

export interface ObstacleMeta {
  type: number;
  center: Point3D;
  heading: number;
  [k: string]: any;
}

export enum TrafficLightColorType {
  PASS = 'pass',
  MAIN = 'main',
  GREY = 'grey',
}

export interface TrafficLightMeta {
  id: string;
  position: Point3D;
  colorType: TrafficLightColorType;
}

/**
 * Sprite 标签池中每个 sprite 挂载的 userData 类型。
 * TrafficLightLabelPool 使用（基础字段）。
 */
export interface SpriteLabelData {
  canvas: HTMLCanvasElement;
  tex: THREE.CanvasTexture;
  dpr: number;
  lastText: string | null;
  assignedId: string | null;
  lastSeenFrame: number;
}

/** 从 Sprite 上安全取出 userData */
export function getSpriteData(sp: THREE.Sprite): SpriteLabelData {
  return (sp as any).userData as SpriteLabelData;
}

export type ExtendedCarInfo = CarInfo & {
  // 车体坐标系下的偏移（例如模型锚点相对于车辆中心的前置偏移）
  vehiclePositionOffset?: Point;
  origin?: Point; // 车辆坐标系原点位置（如果需要在渲染时进行坐标转换）
};

export interface PackedMeta {
  obstacles: ObstacleMeta[];
  // 点云数量统计（worker 中会设置为 message.noisePointInfo.length）
  noisePointsCount: number;
  // 各类窄线在 narrowLinePos 中的位置范围：{ start, count }
  narrowLineRanges: Record<string, { start: number; count: number }>;
  // 各类宽线在 wideLinePos 中的位置范围：{ start, count }
  wideLineRanges: Record<string, { start: number; count: number }>;
  // 自车信息（位置、朝向等） - 在原始 `CarInfo` 基础上扩展渲染所需字段
  carInfo: ExtendedCarInfo;
  /** 障碍物图标范围：每种障碍物类型在 obsAllIcons 中的位置范围 */
  obstacleIconRanges: Record<string, { start: number; count: number }>;
  /** 起/终点图标范围：{ start, count }，key 为 'start' 或 'end' */
  startEndRanges?: Record<string, { start: number; count: number }>;
  /** 红绿灯图标范围：{ start, count }，key 为 'main' | 'grey' | 'pass' */
  trafficLightIconRanges: Record<string, { start: number; count: number }>;
  /** 红绿灯元信息列表 */
  trafficLight: TrafficLightMeta[];
  /** 规划关注的红绿灯列表（包含 id、位置、类型等信息） */
  planningInterestedLights: PlanningTrafficLight[];
  /** MCP（多视角相机/元信息）：用于更新全局 UI（天气/时间/区域等） */
  mcp: Record<string, any> | null;
  /** Worker 端时间戳（可选）：worker 接收时间/解包完成时间，单位 ms（performance.now） */
  workerRecvTs?: number;
  workerDecodeTs?: number;
  workerRecvEpoch?: number;
  workerDecodeEpoch?: number;
}

/**
 * MeshPool 在运行时维护的合并渲染对象集合的类型描述
 * - 保留索引签名以便将来扩展其他合并对象
 */
export interface MeshPoolObjects {
  obsLine: THREE.LineSegments;
  obsSurface: THREE.Mesh;
  globalLines: THREE.LineSegments;
  noisePoints: THREE.Points;
}

/**
 * MeshPool 实例的简要类型（构造时传入 scene，并持有 meshPool 对象）
 */
export interface MeshPoolInstance {
  scene: THREE.Scene;
  meshPool: MeshPoolObjects;
}
